import type { FC } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import CorePickerStar from '@wix/da-ds/pkg/Icons/CorePickerStar';
import CorePickerTwinkle from '@wix/da-ds/pkg/Icons/CorePickerTwinkle';
import CorePickerStarCross from '@wix/da-ds/pkg/Icons/CorePickerStarCross';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import {
  PapiDacheckoutCorePackages,
  PapiSubscriptionV3,
} from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { URLS, sanitizeUrl } from '@wix/da-url';
import BackButton from '../../../BackButton';
import { type PackagePickerStoreSlice } from '../../../../redux/packagePicker/types';
import SaleCountdown from '../SaleCountdown';
import OriginalHeroHeader from './HeroHeader';
import SaleHeroHeader from './SaleHeroHeader';
import HeroTableDesktop from './HeroTableDesktop';
import HeroTableMobile from './HeroTableMobile';
import { getSaleEndDate } from '../../utils';
import DiscontinuedPlanDisclaimer from './_partials/DiscontinuedPlanDisclaimer';
import { getCoreLevel } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { useSelector } from 'react-redux';

import s from './Hero.scss';

export interface Props {
  userJoinDate?: string;
  hasCore?: boolean;
  giftUser?: PackagePickerStoreSlice['giftUser'];
  referer: string;
  currentCoreSubscription?: PapiSubscriptionV3;
  packageBenefits?: PapiDacheckoutCorePackages['packageBenefits'];
  packageConfig?: PapiDacheckoutCorePackages['packageConfig'];
  sale?: PackagePickerStoreSlice['sale'];
}

const Hero: FC<Props> = ({
  userJoinDate,
  giftUser,
  referer,
  hasCore,
  currentCoreSubscription,
  packageBenefits,
  sale,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const userCoreLevel = useSelector(getCoreLevel);
  const isCoreAccess = userCoreLevel === 'core_access';

  const refererIsSubscriptions = /subscriptions/.test(referer);
  const HeroHeader = sale ? SaleHeroHeader : OriginalHeroHeader;

  const { saleBackgroundUrl, ppShowCountdown, ppShowPreHeader } = sale ?? {};
  const showSubtitle = ppShowPreHeader !== false;
  const safeRefererUrl = sanitizeUrl(referer, URLS.ROOT);
  const safeSaleBackgroundUrl = sanitizeUrl(saleBackgroundUrl);

  const saleEndDate = getSaleEndDate(sale, userJoinDate);

  return (
    <Section
      className={cn(s['root'], saleBackgroundUrl && s['sale-background'])}
      style={{
        backgroundImage: safeSaleBackgroundUrl
          ? `url(${safeSaleBackgroundUrl})`
          : undefined,
      }}
    >
      {refererIsSubscriptions && (
        <div className={s['back']}>
          <BackButton href={safeRefererUrl}>{t('common.back')}</BackButton>
        </div>
      )}
      <CorePickerStar
        className={cn(s['star'], s['star-1'])}
        size={IconSize.HUGE}
        disableDefaultColors
      />
      <CorePickerStar
        className={cn(s['star'], s['star-2'])}
        size={IconSize.HUGE}
        disableDefaultColors
      />
      <CorePickerStarCross
        className={cn(s['star'], s['star-3'])}
        size={IconSize.HUGE}
        disableDefaultColors
      />
      <CorePickerTwinkle
        className={cn(s['twinkle'], s['twinkle-1'])}
        disableDefaultColors
        size={IconSize.SMALL}
      />
      <CorePickerTwinkle
        className={cn(s['twinkle'], s['twinkle-2'])}
        disableDefaultColors
        size={IconSize.SMALL}
      />
      {ppShowCountdown && <SaleCountdown sale={sale} />}
      <HeroHeader
        hasCore={hasCore}
        showSubtitle={showSubtitle}
        giftUser={giftUser}
        currentCoreSubscription={currentCoreSubscription}
        sale={sale}
        className={cn(sale && s['hero-header-sale'])}
      />
      {isMobile ? (
        <HeroTableMobile
          sale={sale}
          saleEndDate={saleEndDate}
          referer={referer}
        />
      ) : (
        <>
          {isCoreAccess && (
            <DiscontinuedPlanDisclaimer
              className={s['discountinued-plan-disclaimer']}
            />
          )}
          <HeroTableDesktop
            sale={sale}
            saleEndDate={saleEndDate}
            referer={referer}
            packageBenefits={packageBenefits}
          />
        </>
      )}
    </Section>
  );
};
Hero.displayName = 'Hero';

export default Hero;
